import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

const emptyForm = {
    url: '',
    alt: '',
    width: '',
    height: '',
    border: '',
    hspace: '',
    vspace: '',
    align: 'left'
};

const ImagePropertiesModal = ({ isOpen, initialValues, onCancel, onSave }) => {
    const [form, setForm] = useState(emptyForm);
    const [lockRatio, setLockRatio] = useState(true);
    const [ratio, setRatio] = useState(null);
    const [naturalSize, setNaturalSize] = useState({ width: '', height: '' });

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const nextForm = { ...emptyForm, ...(initialValues || {}) };
        if (!nextForm.align) {
            nextForm.align = 'left';
        }
        setForm(nextForm);
        setLockRatio(true);

        const widthNum = parseFloat(nextForm.width);
        const heightNum = parseFloat(nextForm.height);
        if (widthNum && heightNum) {
            setRatio(widthNum / heightNum);
            setNaturalSize({ width: nextForm.width, height: nextForm.height });
        } else {
            setRatio(null);
            setNaturalSize({ width: '', height: '' });
        }

        if (nextForm.url) {
            const img = new window.Image();
            img.onload = () => {
                setNaturalSize({
                    width: String(img.naturalWidth),
                    height: String(img.naturalHeight)
                });
                if (img.naturalWidth && img.naturalHeight) {
                    setRatio(img.naturalWidth / img.naturalHeight);
                }
            };
            img.src = nextForm.url;
        }
    }, [isOpen, initialValues]);

    const updateField = (name, value) => {
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (lockRatio && ratio) {
                if (name === 'width' && value !== '') {
                    const widthNum = parseFloat(value);
                    if (!Number.isNaN(widthNum)) {
                        next.height = String(Math.round(widthNum / ratio));
                    }
                }
                if (name === 'height' && value !== '') {
                    const heightNum = parseFloat(value);
                    if (!Number.isNaN(heightNum)) {
                        next.width = String(Math.round(heightNum * ratio));
                    }
                }
            }
            return next;
        });
    };

    const resetSize = () => {
        if (!naturalSize.width || !naturalSize.height) {
            return;
        }
        setForm((prev) => ({
            ...prev,
            width: naturalSize.width,
            height: naturalSize.height
        }));
    };

    const previewStyle = {
        maxWidth: '160px',
        maxHeight: '110px',
        width: form.width ? `${form.width}px` : undefined,
        height: form.height ? `${form.height}px` : undefined,
        border: form.border ? `${form.border}px solid #000` : undefined,
        margin: `${form.vspace || 0}px ${form.hspace || 0}px`,
        objectFit: 'contain',
        float: form.align === 'left' || form.align === 'right' ? form.align : undefined,
        display: form.align === 'center' ? 'block' : undefined,
        marginLeft: form.align === 'center' ? 'auto' : undefined,
        marginRight: form.align === 'center' ? 'auto' : undefined
    };

    const previewBoxClass = form.align === 'center'
        ? 'image-properties-preview-box is-center'
        : form.align === 'right'
            ? 'image-properties-preview-box is-right'
            : 'image-properties-preview-box is-left';

    return (
        <Modal isOpen={isOpen} toggle={onCancel} zIndex={2000} className="image-properties-modal">
            <ModalHeader toggle={onCancel}>Image Properties</ModalHeader>
            <ModalBody>
                <div className="image-properties-layout">
                    <div className="image-properties-fields">
                        <label>
                            URL
                            <input
                                type="url"
                                className="form-control"
                                placeholder="https://example.com/image.png"
                                value={form.url}
                                onChange={(event) => updateField('url', event.target.value)}
                            />
                        </label>
                        <label>
                            Alternative Text
                            <input
                                type="text"
                                className="form-control"
                                value={form.alt}
                                onChange={(event) => updateField('alt', event.target.value)}
                            />
                        </label>
                        <div className="image-properties-size">
                            <label>
                                Width
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    value={form.width}
                                    onChange={(event) => updateField('width', event.target.value)}
                                />
                            </label>
                            <div className="image-properties-lock">
                                <button
                                    type="button"
                                    className={lockRatio ? 'active' : ''}
                                    title={lockRatio ? 'Unlock ratio' : 'Lock ratio'}
                                    onClick={() => setLockRatio((prev) => !prev)}
                                >
                                    {lockRatio ? 'Lock' : 'Unlock'}
                                </button>
                                <button type="button" title="Reset size" onClick={resetSize}>
                                    Reset
                                </button>
                            </div>
                            <label>
                                Height
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    value={form.height}
                                    onChange={(event) => updateField('height', event.target.value)}
                                />
                            </label>
                        </div>
                        <div className="image-properties-layout-fields">
                            <label>
                                Border
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    value={form.border}
                                    onChange={(event) => updateField('border', event.target.value)}
                                />
                            </label>
                            <label>
                                HSpace
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    value={form.hspace}
                                    onChange={(event) => updateField('hspace', event.target.value)}
                                />
                            </label>
                            <label>
                                VSpace
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    value={form.vspace}
                                    onChange={(event) => updateField('vspace', event.target.value)}
                                />
                            </label>
                            <label>
                                Align
                                <select
                                    className="form-control"
                                    value={form.align}
                                    onChange={(event) => updateField('align', event.target.value)}
                                >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                </select>
                            </label>
                        </div>
                    </div>
                    <div className="image-properties-preview">
                        <div>Preview</div>
                        <div className={previewBoxClass}>
                            {form.url ? (
                                <img src={form.url} alt={form.alt || 'preview'} style={previewStyle} />
                            ) : (
                                <span>Lorem ipsum dolor sit amet</span>
                            )}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onCancel}>Cancel</Button>
                <Button color="primary" onClick={() => onSave(form)} disabled={!form.url || form.url.trim().toLowerCase().startsWith('data:')}>OK</Button>
            </ModalFooter>
        </Modal>
    );
};

export default ImagePropertiesModal;
